import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type {
  GuestRequest,
  GuestRequestTransition,
} from "@shared/types/guestRequest";
import { tokens } from "../../theme";
import {
  TRANSITION_LABEL,
  transitionConsequence,
  transitionTakesReason,
  transitionTitle,
} from "./guestRequestCopy";

/** Long enough to say why, short enough to read across a hotel room. Matches
 *  `REASON_MAX_CHARS` on the server, which trims anything longer anyway. */
const REASON_MAX = 200;

interface Props {
  request: GuestRequest;
  transition: GuestRequestTransition;
  /** How the queue names this room, so the sentence names the same thing the
   *  row does — "412", not a Mongo id. */
  roomLabel: string;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
}

/**
 * The step between pressing a verb and it happening (`POS-9`).
 *
 * ## Why Accept is not a one-click button
 *
 * Accepting is **the revenue event** (`docs/room-offers.md` §5): POS-11 counts
 * accepted requests, and on a charge-to-room order it is the moment a guest
 * owes the hotel money. A button that books 450 kr to somebody's folio without
 * saying so is a button whose consequence a staff member has to have been
 * taught — and the one who was not taught is the one working the Sunday shift.
 * So every verb states what it does, in a sentence, before it does it.
 *
 * ## Declining is exactly as easy as accepting
 *
 * Same dialog, same number of clicks, and the reason is **optional**: a decline
 * gated behind a mandatory explanation is a decline nobody makes, and the
 * ticket is explicit that a staff member who cannot decline will simply ignore
 * the queue. What the reason buys when it is given is that POS-7 puts it on the
 * guest's television, so a refusal arrives as a sentence rather than as a
 * status that silently changed colour.
 *
 * Seeded once and remounted by key from the queue
 * (`docs/memory/global/conventions/cms-list-and-editor.md` rule 2) — the
 * remount IS the reset, so a reason typed for one row can never follow the
 * operator to the next one.
 */
export default function GuestRequestActionDialog({
  request,
  transition,
  roomLabel,
  busy,
  error,
  onClose,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState("");
  const takesReason = transitionTakesReason(transition);

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      data-testid="request-action-dialog"
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {transitionTitle(transition, request)}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography
            sx={{ fontSize: 14, color: tokens.ink }}
            data-testid="request-action-consequence"
          >
            {transitionConsequence(transition, request, roomLabel)}
          </Typography>
          {takesReason && (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Reason (optional)"
              value={reason}
              inputProps={{ maxLength: REASON_MAX }}
              onChange={(e) => setReason(e.target.value)}
              helperText="Shown to the guest on their television. Leave it empty to say nothing."
              data-testid="request-action-reason"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Back
        </Button>
        <Button
          variant="contained"
          color={transition === "accept" ? "primary" : "inherit"}
          disabled={busy}
          onClick={() => onConfirm(reason.trim() || undefined)}
          data-testid="request-action-confirm"
        >
          {busy ? "Working…" : TRANSITION_LABEL[transition]}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
